(() => {
  const instances = new WeakMap();
  const storagePrefix = 'japanime:collection-scroll:v24:';

  class JapanimeInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.loading = false;
      this.pagesLoaded = 1;
      this.observer = null;
      this.boundPageHide = this.save.bind(this);
      this.boundProductClick = this.onProductClick.bind(this);
      this.root.addEventListener('click', this.boundProductClick, true);
      window.addEventListener('pagehide', this.boundPageHide);
      this.refreshDom();
      this.restore();
    }

    get storageKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      return `${storagePrefix}${url.pathname}${url.search}`;
    }

    refreshDom() {
      this.grid = this.root.querySelector('#product-grid > ul') || this.root.querySelector('#product-grid');
      this.control = this.root.querySelector('[data-jp-infinite-scroll]');
      this.nextLink = this.root.querySelector('[data-jp-infinite-next]');
      this.status = this.root.querySelector('[data-jp-infinite-status]');
      this.pagination = this.root.querySelector('[data-jp-pagination-fallback]');

      if (this.observer) this.observer.disconnect();
      if (!this.grid || !this.control) return;

      if ('IntersectionObserver' in window && this.nextLink) {
        if (this.pagination) this.pagination.hidden = true;
        this.nextLink.hidden = true;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: '900px 0px' },
        );
        this.observer.observe(this.control);
      } else if (this.nextLink) {
        this.nextLink.hidden = false;
        this.nextLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.loadNext();
        });
      }
    }

    readState() {
      try {
        const value = window.sessionStorage.getItem(this.storageKey);
        if (!value) return null;
        const state = JSON.parse(value);
        if (!state || Date.now() - state.savedAt > 6 * 60 * 60 * 1000) return null;
        return state;
      } catch (error) {
        return null;
      }
    }

    save() {
      if (!this.grid) return;
      try {
        window.sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            pagesLoaded: this.pagesLoaded,
            scrollY: Math.max(0, Math.round(window.scrollY)),
            savedAt: Date.now(),
          }),
        );
      } catch (error) {
        // Storage can be unavailable in privacy mode; infinite scroll still works.
      }
    }

    onProductClick(event) {
      if (event.target.closest('a[href*="/products/"]')) this.save();
    }

    async restore() {
      const state = this.readState();
      if (!state) return;

      const targetPages = Math.max(1, Math.min(Number(state.pagesLoaded) || 1, 12));
      while (this.pagesLoaded < targetPages && this.nextLink) {
        const loaded = await this.loadNext({ restoring: true });
        if (!loaded) break;
      }

      if (this.nextLink) this.setStatus('');

      if (Number.isFinite(state.scrollY)) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => window.scrollTo({ top: state.scrollY, behavior: 'auto' }));
        });
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    async loadNext({ restoring = false } = {}) {
      if (this.loading || !this.nextLink || !this.grid) return false;
      this.loading = true;
      const nextUrl = this.nextLink.href;
      this.root.classList.add('is-loading-more');
      this.setStatus(this.root.dataset.jpLoadingLabel || 'Loading products…');

      try {
        const response = await fetch(nextUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const documentFromNextPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid =
          documentFromNextPage.querySelector('#product-grid > ul') || documentFromNextPage.querySelector('#product-grid');
        if (!nextGrid) throw new Error('Product grid not found');

        const knownProducts = new Set(
          Array.from(this.grid.querySelectorAll('a[href*="/products/"]')).map((link) => link.pathname),
        );
        const fragment = document.createDocumentFragment();

        nextGrid.querySelectorAll(':scope > li').forEach((item) => {
          const productLink = item.querySelector('a[href*="/products/"]');
          if (productLink && knownProducts.has(productLink.pathname)) return;
          item.classList.add('scroll-trigger--cancel');
          fragment.appendChild(document.importNode(item, true));
        });

        this.grid.appendChild(fragment);
        this.pagesLoaded += 1;

        const followingLink = documentFromNextPage.querySelector('[data-jp-infinite-next]');
        if (followingLink) {
          this.nextLink.href = followingLink.href;
        } else {
          if (this.observer) this.observer.disconnect();
          this.nextLink.remove();
          this.nextLink = null;
          this.setStatus(this.root.dataset.jpCompleteLabel || 'All products loaded');
        }

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        document.dispatchEvent(new CustomEvent('japanime:products-appended', { detail: { root: this.root } }));
        if (!restoring) this.save();
        return true;
      } catch (error) {
        console.error('[Japanime infinite scroll]', error);
        this.setStatus('');
        if (this.nextLink) this.nextLink.hidden = false;
        if (this.pagination) this.pagination.hidden = false;
        if (this.observer) this.observer.disconnect();
        return false;
      } finally {
        this.loading = false;
        this.root.classList.remove('is-loading-more');
        if (this.nextLink && !restoring) this.setStatus('');
      }
    }

    resetAfterFiltering() {
      this.pagesLoaded = 1;
      this.refreshDom();
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // No action needed when session storage is unavailable.
      }
    }
  }

  const initialize = () => {
    document.querySelectorAll('[data-jp-infinite-root]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new JapanimeInfiniteScroll(root));
    });
  };

  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('japanime:collection-updated', () => {
    document.querySelectorAll('[data-jp-infinite-root]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.resetAfterFiltering();
    });
  });
})();
